// Compact storage for rendered frames: grayscale frames (B/W SIRDS) keep one
// byte per pixel, colour frames keep RGBA.

import type { RgbaData } from '../types';

export interface StoredFrame {
  channels: 1 | 4;
  data: Uint8Array;
}

export class FrameStore {
  readonly frames: (StoredFrame | undefined)[];

  constructor(
    readonly width: number,
    readonly height: number,
    count: number,
  ) {
    this.frames = new Array(count);
  }

  get count(): number {
    return this.frames.length;
  }

  get ready(): number {
    return this.frames.reduce((n, f) => n + (f ? 1 : 0), 0);
  }

  set(i: number, img: RgbaData) {
    const src = img.data;
    let gray = true;
    for (let p = 0; p < src.length; p += 4) {
      if (src[p] !== src[p + 1] || src[p] !== src[p + 2]) {
        gray = false;
        break;
      }
    }
    if (gray) {
      const g = new Uint8Array(src.length / 4);
      for (let p = 0, q = 0; q < g.length; p += 4, q++) g[q] = src[p];
      this.frames[i] = { channels: 1, data: g };
    } else {
      this.frames[i] = { channels: 4, data: src instanceof Uint8Array ? src : new Uint8Array(src.buffer) };
    }
  }

  /** Expands a frame to RGBA (new buffer). */
  rgba(i: number): Uint8ClampedArray<ArrayBuffer> | null {
    const f = this.frames[i];
    if (!f) return null;
    if (f.channels === 4)
      return new Uint8ClampedArray(
        f.data.buffer.slice(f.data.byteOffset, f.data.byteOffset + f.data.byteLength) as ArrayBuffer,
      );
    const out = new Uint8ClampedArray(f.data.length * 4);
    for (let q = 0, p = 0; q < f.data.length; q++, p += 4) {
      const v = f.data[q];
      out[p] = out[p + 1] = out[p + 2] = v;
      out[p + 3] = 255;
    }
    return out;
  }

  bytes(): number {
    return this.frames.reduce((n, f) => n + (f ? f.data.byteLength : 0), 0);
  }
}

/** Grayscale preview store for depth frames. */
export function depthStore(frames: { width: number; height: number; data: Uint16Array }[]): FrameStore {
  const w = frames[0]?.width ?? 1;
  const h = frames[0]?.height ?? 1;
  const store = new FrameStore(w, h, frames.length);
  frames.forEach((f, i) => {
    const g = new Uint8Array(f.data.length);
    for (let k = 0; k < g.length; k++) g[k] = f.data[k] >> 8;
    store.frames[i] = { channels: 1, data: g };
  });
  return store;
}
